import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';

import type { HeadingSanityBlockType } from '@/components/common/Heading';
import type { SpacingScaleNumber } from '@/types/tokens';
import type { SanityBlock, SanityKeyed } from 'sanity-codegen';

export type AlignmentOptions = 'start' | 'center' | 'end';

type PaddingOptions = {
  caption?: SpacingScaleNumber;
  captionMobile?: SpacingScaleNumber;
  description?: SpacingScaleNumber;
  descriptionMobile?: SpacingScaleNumber;
};

export type CaptionHeadingDescriptionProps = {
  captionHeadingDescription?: {
    caption?: string;
    heading?: HeadingSanityBlockType;
    description?: Array<SanityKeyed<SanityBlock>> | string;
  };
  desktopAlignment?: AlignmentOptions;
  mobileAlignment?: AlignmentOptions;
  padding?: PaddingOptions;
  isCaptionUppercase?: boolean;
  className?: string;
};

export const CaptionHeadingDescription = ({
  captionHeadingDescription,
  desktopAlignment = 'start',
  mobileAlignment = 'start',
  padding,
  isCaptionUppercase = true,
  className,
}: CaptionHeadingDescriptionProps) => {
  const caption = captionHeadingDescription?.caption;
  const heading = captionHeadingDescription?.heading;
  const description = captionHeadingDescription?.description;

  return (
    <CaptionHeadingDescriptionContainer
      desktopAlignment={desktopAlignment}
      mobileAlignment={mobileAlignment}
      className={className}
    >
      {caption && (
        <Caption
          size="caption-lg"
          className="caption"
          paddingBottom={padding?.caption || 100}
          mobilePaddingBottom={padding?.captionMobile || 150}
          isCaptionUppercase={isCaptionUppercase}
        >
          {caption}
        </Caption>
      )}
      {heading && <RichHeading heading={heading} size="headline-md" />}
      {description && (
        <DescriptionContainer
          className="description"
          paddingTop={padding?.description || 100}
          mobilePaddingTop={padding?.descriptionMobile || 50}
        >
          {typeof description === 'string' ? (
            <Paragraph size="body-md">{description}</Paragraph>
          ) : (
            <PortableText value={description} />
          )}
        </DescriptionContainer>
      )}
    </CaptionHeadingDescriptionContainer>
  );
};

const CaptionHeadingDescriptionContainer = styled.div<{
  mobileAlignment: AlignmentOptions;
  desktopAlignment: AlignmentOptions;
}>`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--spacing-x-large);

  align-items: ${({ mobileAlignment }) => `${mobileAlignment}`};
  text-align: ${({ mobileAlignment }) => `${mobileAlignment}`};
  justify-items: ${({ mobileAlignment }) => `${mobileAlignment}`};

  ${screen.md} {
    align-items: ${({ desktopAlignment }) => `${desktopAlignment}`};
    text-align: ${({ desktopAlignment }) => `${desktopAlignment}`};
    justify-items: ${({ desktopAlignment }) => `${desktopAlignment}`};
  }
`;

const Caption = styled(Paragraph)<{
  paddingBottom: SpacingScaleNumber;
  mobilePaddingBottom: SpacingScaleNumber;
  isCaptionUppercase: boolean;
}>`
  color: var(--text-action);
  text-transform: ${({ isCaptionUppercase }) =>
    isCaptionUppercase ? 'uppercase' : 'none'};

  ${(p) =>
    p.mobilePaddingBottom &&
    `padding-bottom: var(--spacing-${p.mobilePaddingBottom});`}
  ${screen.md} {
    ${(p) =>
      p.paddingBottom && `padding-bottom: var(--spacing-${p.paddingBottom});`}
  }
`;

const DescriptionContainer = styled.div<{
  paddingTop: SpacingScaleNumber;
  mobilePaddingTop: SpacingScaleNumber;
}>`
  ${(p) =>
    p.mobilePaddingTop && `padding-top: var(--spacing-${p.mobilePaddingTop});`}
  ${screen.lg} {
    ${(p) => p.paddingTop && `padding-top: var(--spacing-${p.paddingTop});`}
  }
`;
